package com.swetdot.swetgram.ui.design

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.geometry.isSpecified
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.layout.ContentScale
import coil3.compose.AsyncImagePainter
import coil3.compose.rememberAsyncImagePainter

/**
 * The design system's one point of contact with the image loader.
 *
 * Every remote picture in Swetgram -- a message photo, an avatar, a QR code -- goes through
 * [rememberSwetImage], for the same reason every animation goes through `swetTween`: the
 * alternative is not "no policy", it is each screen picking its own loading and failure treatment
 * and its own idea of what an unreachable image looks like.
 *
 * Coil's own `AsyncImage` and `SubcomposeAsyncImage` are deliberately NOT used.
 * `SubcomposeAsyncImage` is the one that takes `loading`/`error` slots, and Coil's own
 * documentation says not to put it in a `LazyColumn` -- which is precisely where the message
 * timeline and the chat list live. This returns the three states as data instead, so the caller
 * draws them with ordinary composables and no subcomposition happens at all.
 */

/** What a remote image is doing. Coil's four states, reduced to the three a screen must draw. */
enum class SwetImagePhase {
    /** Requested, not yet decoded. The caller shows a skeleton -- never a spinner (SDS §2.1). */
    Loading,

    /** Decoded and ready. [SwetImage.painter] may be drawn. */
    Loaded,

    /** Unreachable, rejected or undecodable. The caller shows something a person can read. */
    Failed,
}

/**
 * A remote image and what is currently known about it.
 *
 * [aspectRatio] is width / height of the DECODED image and is null until [phase] is
 * [SwetImagePhase.Loaded]. It is exposed because a chat photo has to be laid out at its own
 * proportions, and that cannot be known before the bytes arrive -- a caller that needs a stable box
 * before then must supply its own placeholder ratio.
 */
class SwetImage internal constructor(
    val painter: Painter,
    val phase: SwetImagePhase,
    val aspectRatio: Float?,
)

/**
 * Starts loading [url] and reports progress as [SwetImage].
 *
 * The request is cancelled automatically when this leaves the composition, so a fast scroll does
 * not leave a screen's worth of downloads running.
 *
 * [contentScale] must match the one passed to the `Image` that eventually draws
 * [SwetImage.painter]: Coil sizes its request from it, and a mismatch decodes at the wrong
 * resolution.
 */
@Composable
fun rememberSwetImage(
    url: String,
    contentScale: ContentScale = ContentScale.Crop,
): SwetImage {
    val painter = rememberAsyncImagePainter(model = url, contentScale = contentScale)
    val state by painter.state.collectAsState()
    return when (val current = state) {
        is AsyncImagePainter.State.Success ->
            SwetImage(painter, SwetImagePhase.Loaded, current.painter.intrinsicSize.ratioOrNull())

        is AsyncImagePainter.State.Error -> SwetImage(painter, SwetImagePhase.Failed, null)

        // Empty is the state before the request is dispatched, which from the reader's point of
        // view is indistinguishable from loading -- collapsing the two here stops every call site
        // from having to draw a fourth thing that lasts one frame.
        else -> SwetImage(painter, SwetImagePhase.Loading, null)
    }
}

/**
 * width / height, or null when the painter cannot say.
 *
 * A vector or a still-empty painter reports [Size.Unspecified], and some report an infinite
 * dimension; both would produce a NaN or infinite `aspectRatio` modifier, which throws at layout
 * rather than degrading. Null lets the caller fall back to a ratio it chose.
 */
private fun Size.ratioOrNull(): Float? {
    if (!isSpecified) return null
    if (!width.isFinite() || !height.isFinite()) return null
    if (width <= 0f || height <= 0f) return null
    return width / height
}

/**
 * Colours for anything drawn ON TOP of a photo.
 *
 * A photo is not a themed surface: it is arbitrary pixels the sender chose, and no token in
 * [SwetgramColors] is legible against all of them -- `text.primary` is near-white in dark and
 * near-black in light, and each one disappears against half the photographs in the world. So
 * content drawn over an image gets its own two-colour pair, and it is deliberately the SAME pair
 * in both themes: the thing underneath does not change when the theme does.
 *
 * These live in the design system rather than in the chat screen because the problem is not the
 * chat screen's -- it belongs to every surface that puts a label over a picture.
 */
object SwetOnImage {

    /**
     * Text and glyphs drawn over [scrim]. White, always, in both themes.
     *
     * NOT `Color.White.copy(alpha = ...)` the way outgoing-bubble metadata is dimmed: that bubble
     * is a known accent blue and can carry a reduced-opacity label, whereas here the backdrop is a
     * 55%-black film over an unknown photo. White at 72% over that film measures about 3.3:1
     * against the worst case, which fails AA at caption size; full white is the 4.7:1 below.
     */
    val content: Color = Color.White

    /**
     * The film a label sits on so it can be read against any photograph.
     *
     * The alpha is derived, not chosen by eye. The hostile case is a photo that is pure white
     * exactly where the label lands -- a snow field, a document, an overexposed sky. Composite
     * black at [SCRIM_ALPHA] over white and the result is #737373, against which [content]
     * measures 4.74:1: past WCAG AA's 4.5:1 for the 12sp `caption` the timestamp is set in
     * (SDS §22). Anything darker than white in the photo only improves that, so this is a floor
     * rather than a typical value. Lowering the alpha for looks breaks the guarantee.
     */
    val scrim: Color = Color.Black.copy(alpha = SCRIM_ALPHA)
}

private const val SCRIM_ALPHA = 0.55f
